import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AZURE } from '../../services/api';
import { VUKZ } from '../../theme/vukzTheme';
import {
  StudyBreadcrumb,
  StudyHeroSection,
  StudyFilterPanel,
  StudyChartBlock,
  StudyInsightCallout,
  StudyDataTable,
  StudyMethodologyBox,
  StudyConclusionBox,
  StudyFooter,
} from '../../components/studies';
import { Users, Clock, AlertTriangle } from 'lucide-react';
import {
  SWIM_CATEGORY_COLORS,
  SWIM_CATEGORY_SORT,
  SWIM_CATEGORY_DISPLAY,
  sortSwimCategories,
  getSwimCategoryColor,
} from '../../config/sportsConfig';

/**
 * StudyAvgTimesEvolution — Primeiro estudo interativo da ArenaStats
 *
 * "Evolução do Tempo Médio por Prova — Natação Brasil"
 *
 * Consome: GET /api/editorial/studies/avg-times-evolution
 * Returns coverage-aware data:
 *   - chart_series: only categories with complete year coverage
 *   - all_series: all data points (for tables)
 *   - coverage_report: category coverage breakdown
 *
 * Categories sorted by chronological development:
 *   Infantil 1 → Infantil 2 → Juvenil 1 → Juvenil 2 → Junior 1 → Junior 2 → Absoluto
 */

const GENDER_LABELS = { M: 'Masculino', F: 'Feminino' };

// Format seconds to mm:ss.cc
const formatTime = (seconds) => {
  if (!seconds || seconds <= 0) return '—';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins > 0) return `${mins}:${secs.toFixed(2).padStart(5, '0')}`;
  return `${secs.toFixed(2)}s`;
};

/**
 * Gera interpretações didáticas automáticas com base nos dados de um grupo.
 * Em produção, essas interpretações viriam do campo chart_blocks_json do artigo.
 * Aqui são geradas dinamicamente para o primeiro estudo interativo.
 */
const generateInterpretations = (group) => {
  const { chartData, categories, event_name, gender } = group;
  const genderLabel = GENDER_LABELS[gender] || gender;

  if (!chartData.length || !categories.length) {
    return {
      question: `Como evoluíram os tempos de ${event_name} (${genderLabel})?`,
      whatItShows: 'Dados insuficientes para gerar interpretação automática.',
      whatStandsOut: null,
      practicalMeaning: null,
    };
  }

  const firstYear = chartData[0]?.year;
  const lastYear = chartData[chartData.length - 1]?.year;
  const mainCategory = categories.includes('Senior') ? 'Senior' : categories[0];
  const firstVal = chartData[0]?.[mainCategory];
  const lastVal = chartData[chartData.length - 1]?.[mainCategory];

  // Compute trend for main category
  let trendText = '';
  if (firstVal && lastVal) {
    const diff = ((firstVal - lastVal) / firstVal) * 100;
    if (diff > 0) {
      trendText = `melhora de ${diff.toFixed(1)}% (de ${formatTime(firstVal)} para ${formatTime(lastVal)})`;
    } else if (diff < 0) {
      trendText = `piora de ${Math.abs(diff).toFixed(1)}% (de ${formatTime(firstVal)} para ${formatTime(lastVal)})`;
    } else {
      trendText = `estabilidade em torno de ${formatTime(lastVal)}`;
    }
  }

  // Find fastest improvement year
  let bestImprovement = { year: null, delta: 0 };
  for (let i = 1; i < chartData.length; i++) {
    const prev = chartData[i - 1]?.[mainCategory];
    const curr = chartData[i]?.[mainCategory];
    if (prev && curr) {
      const delta = prev - curr;
      if (delta > bestImprovement.delta) {
        bestImprovement = { year: chartData[i].year, delta };
      }
    }
  }

  return {
    question: `Os tempos de ${event_name} (${genderLabel}) estão melhorando ao longo dos anos?`,
    whatItShows: trendText
      ? `A série de ${firstYear} a ${lastYear} mostra ${trendText} para a categoria ${mainCategory}, com ${chartData.length} anos de dados e ${categories.length} categorias etárias comparadas.`
      : `Dados de ${firstYear} a ${lastYear} para ${categories.length} categorias etárias.`,
    whatStandsOut: bestImprovement.year
      ? `A maior melhora aconteceu em ${bestImprovement.year}, com queda de ${formatTime(bestImprovement.delta)} no tempo médio da categoria ${mainCategory}.`
      : 'Não foi possível identificar um ano de melhora significativa nos dados disponíveis.',
    practicalMeaning: categories.length > 1
      ? `Com ${categories.length} categorias etárias na mesma prova, técnicos podem comparar a progressão natural dos atletas entre categorias e identificar se o desenvolvimento está no ritmo esperado.`
      : `Acompanhar a evolução temporal permite ajustar metas de treino e tempos de corte para seletivas.`,
  };
};

const StudyAvgTimesEvolution = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [distance, setDistance] = useState('');
  const [stroke, setStroke] = useState('');
  const [gender, setGender] = useState('');
  const [ageCategory, setAgeCategory] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (distance) params.distance = distance;
      if (stroke) params.stroke = stroke;
      if (gender) params.gender = gender;
      if (ageCategory) params.age_category = ageCategory;

      const { data: resp } = await AZURE.get('/editorial/studies/avg-times-evolution', { params });
      if (resp.success) {
        setData(resp.data);
      } else {
        setError('Erro ao carregar dados do estudo');
      }
    } catch (err) {
      console.error('Error fetching study data:', err);
      setError('Erro ao carregar dados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [distance, stroke, gender, ageCategory]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filters = data?.available_filters || {};
  const meta = data?.meta || {};
  const chartSeries = data?.chart_series || [];
  const allSeries = data?.all_series || [];
  const coverageReport = data?.coverage_report || [];

  // Group chart-eligible series into chart-ready format
  const chartGroups = useMemo(() => {
    if (!chartSeries.length) return [];

    const groups = {};
    chartSeries.forEach((row) => {
      const key = `${row.event_name}__${row.gender}`;
      if (!groups[key]) {
        groups[key] = {
          event_name: row.event_name,
          gender: row.gender,
          distance_meters: row.distance_meters,
          stroke: row.stroke,
          categories: new Set(),
          byYear: {},
        };
      }
      groups[key].categories.add(row.age_category);
      if (!groups[key].byYear[row.competition_year]) {
        groups[key].byYear[row.competition_year] = { year: row.competition_year };
      }
      groups[key].byYear[row.competition_year][row.age_category] = row.avg_time_seconds;
      groups[key].byYear[row.competition_year][`${row.age_category}_best`] = row.best_time_seconds;
      groups[key].byYear[row.competition_year][`${row.age_category}_count`] = row.total_results;
    });

    return Object.values(groups).map((g) => ({
      ...g,
      categories: sortSwimCategories(Array.from(g.categories)),
      chartData: Object.values(g.byYear).sort((a, b) => a.year - b.year),
    }));
  }, [chartSeries]);

  // Coverage summary per event/gender
  const coverageSummary = useMemo(() => {
    if (!coverageReport.length) return {};
    const summary = {};
    coverageReport.forEach((row) => {
      const key = `${row.event_name}__${row.gender}`;
      if (!summary[key]) {
        summary[key] = { total: 0, complete: 0, partial: 0, insufficient: 0 };
      }
      summary[key].total++;
      if (row.coverage_status === 'complete') summary[key].complete++;
      else if (row.coverage_status === 'partial') summary[key].partial++;
      else summary[key].insufficient++;
    });
    return summary;
  }, [coverageReport]);

  // Summary statistics
  const summaryStats = useMemo(() => {
    if (!allSeries.length) return null;
    const totalResults = allSeries.reduce((sum, r) => sum + (r.total_results || 0), 0);
    const totalSwimmers = allSeries.reduce((sum, r) => sum + (r.total_swimmers || 0), 0);
    const yearRange = allSeries.reduce(
      (acc, r) => ({
        min: Math.min(acc.min, r.competition_year || Infinity),
        max: Math.max(acc.max, r.competition_year || 0),
      }),
      { min: Infinity, max: 0 }
    );
    return { totalResults, totalSwimmers, yearRange };
  }, [allSeries]);

  // Filter config for StudyFilterPanel
  const hasActiveFilters = !!(distance || stroke || gender || ageCategory);
  const filterConfig = [
    {
      key: 'distance',
      label: 'Distância',
      value: distance,
      onChange: setDistance,
      placeholder: 'Todas',
      options: (filters.distances || []).map((d) => ({ value: d, label: `${d}m` })),
    },
    {
      key: 'stroke',
      label: 'Estilo',
      value: stroke,
      onChange: setStroke,
      placeholder: 'Todos',
      options: (filters.strokes || []).map((s) => ({ value: s, label: s })),
    },
    {
      key: 'gender',
      label: 'Gênero',
      value: gender,
      onChange: setGender,
      placeholder: 'Todos',
      options: (filters.genders || []).map((g) => ({ value: g, label: GENDER_LABELS[g] || g })),
    },
    {
      key: 'ageCategory',
      label: 'Categoria',
      value: ageCategory,
      onChange: setAgeCategory,
      placeholder: 'Todas',
      options: (filters.age_categories || []).map((c) => ({ value: c, label: c })),
    },
  ];

  const handleClearFilters = () => {
    setDistance('');
    setStroke('');
    setGender('');
    setAgeCategory('');
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: VUKZ.bg }}>
      {/* Breadcrumb */}
      <StudyBreadcrumb studyTitle="Evolução dos Tempos" />

      {/* Hero */}
      <StudyHeroSection
        emoji="🏊"
        title="Evolução do Tempo Médio por Prova"
        description="Análise aprofundada da evolução dos tempos médios na natação brasileira, segmentado por gênero, categoria etária e tipo de prova. Dados reais de competições oficiais processados pela ArenaStats."
        category="natacao"
        stats={
          summaryStats
            ? [
                { value: summaryStats.totalResults, label: 'Resultados analisados' },
                { value: summaryStats.totalSwimmers, label: 'Participações' },
                {
                  value:
                    summaryStats.yearRange.min === Infinity
                      ? '—'
                      : `${summaryStats.yearRange.min}–${summaryStats.yearRange.max}`,
                  label: 'Período',
                },
                { value: chartGroups.length, label: 'Séries' },
              ]
            : []
        }
      />

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-8">
        {/* Executive summary callout */}
        <StudyInsightCallout variant="insight" title="Resumo executivo">
          Este estudo analisa a evolução dos tempos médios na natação competitiva brasileira,
          comparando categorias etárias (Infantil a Senior) ao longo dos anos. Os dados provêm
          de competições oficiais registradas no sistema ArenaStats e permitem identificar tendências
          de melhora, estagnação ou regressão por prova, gênero e faixa etária.
        </StudyInsightCallout>

        {/* Filters */}
        <StudyFilterPanel
          filters={filterConfig}
          hasActiveFilters={hasActiveFilters}
          onClear={handleClearFilters}
        />

        {/* Content */}
        {loading ? (
          <div className="space-y-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white h-80 rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <p className="text-gray-400 text-lg mb-4">{error}</p>
            <button
              onClick={fetchData}
              className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-semibold hover:bg-gray-800"
            >
              Tentar novamente
            </button>
          </div>
        ) : chartGroups.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-400 text-lg">
              Nenhum dado encontrado para os filtros selecionados.
            </p>
            <p className="text-gray-300 text-sm mt-2">Tente ajustar os filtros acima.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {chartGroups.map((group, idx) => {
              const interp = generateInterpretations(group);
              const genderLabel = GENDER_LABELS[group.gender] || group.gender;

              // Build series config for StudyChartBlock (using centralized colors)
              const chartSeries = group.categories.map((cat) => ({
                dataKey: cat,
                name: SWIM_CATEGORY_DISPLAY[cat] || cat,
                color: getSwimCategoryColor(cat),
              }));

              // Build data table columns (sorted by category order)
              const tableColumns = [
                { key: 'year', label: 'Ano', align: 'left' },
                ...group.categories.map((cat) => ({
                  key: cat,
                  label: SWIM_CATEGORY_DISPLAY[cat] || cat,
                  color: getSwimCategoryColor(cat),
                  format: (v) => (v ? formatTime(v) : '—'),
                })),
              ];

              // Coverage info for this group
              const covKey = `${group.event_name}__${group.gender}`;
              const cov = coverageSummary[covKey];

              return (
                <React.Fragment key={`${group.event_name}__${group.gender}`}>
                  <StudyChartBlock
                    chartType="line"
                    title={`${group.event_name} — ${genderLabel}`}
                    question={interp.question}
                    data={group.chartData}
                    series={chartSeries}
                    xField="year"
                    formatY={formatTime}
                    formatTooltip={(value, name) => [formatTime(value), name]}
                    reversedY
                    whatItShows={interp.whatItShows}
                    whatStandsOut={interp.whatStandsOut}
                    practicalMeaning={interp.practicalMeaning}
                  >
                    {/* Legend pills above data table */}
                    <div className="px-6 pb-2 flex flex-wrap items-center gap-2">
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {genderLabel}
                      </span>
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {group.chartData.length} anos de dados
                      </span>
                      <span className="text-xs text-gray-400">
                        {group.distance_meters}m • {group.stroke}
                      </span>
                    </div>

                    {/* Coverage notice */}
                    {cov && cov.total > cov.complete && (
                      <div className="mx-6 mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-amber-700">
                          {cov.complete} de {cov.total} categorias têm cobertura completa entre todos os anos.
                          {cov.partial > 0 && ` ${cov.partial} categoria(s) com cobertura parcial foram omitidas do gráfico.`}
                          {cov.insufficient > 0 && ` ${cov.insufficient} com dados insuficientes.`}
                        </p>
                      </div>
                    )}

                    {/* Data table */}
                    <StudyDataTable
                      data={group.chartData}
                      columns={tableColumns}
                      caption={`Tempos médios por categoria — ${group.event_name} (${genderLabel}). Valores em mm:ss.cc`}
                    />
                  </StudyChartBlock>

                  {/* Insert methodology note after the first chart */}
                  {idx === 0 && chartGroups.length > 1 && (
                    <StudyInsightCallout variant="positive" title="Nota de integridade dos dados">
                      Os gráficos abaixo exibem apenas categorias com cobertura completa em todos
                      os anos da janela analisada. Categorias com dados parciais ou amostra
                      insuficiente (&lt;3 resultados) aparecem apenas na tabela detalhada.
                    </StudyInsightCallout>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}

        {/* Conclusions */}
        {!loading && chartGroups.length > 0 && (
          <StudyConclusionBox
            title="Conclusões e Implicações"
            summary="Os dados de 2023 a 2025 mostram um cenário misto. Em algumas provas e categorias há melhora dos tempos médios, especialmente em parte das faixas de formação, mas essa evolução não aparece de forma uniforme em toda a base analisada. Também há oscilações importantes entre anos e lacunas em algumas categorias, o que recomenda cautela na interpretação."
            takeaways={[
              'Há indícios localizados de melhora, mas o panorama geral é de oscilação e heterogeneidade entre categorias.',
              'Categorias com cobertura parcial foram excluídas do gráfico — consultar tabela abaixo para dados completos.',
              'Mínimo de 3 resultados por ponto de dado para evitar conclusões baseadas em amostras frágeis.',
              'Categorias derivadas do nome da competição + idade CBDA (birth_date). Sub-categorias 1/2 dependem de data de nascimento disponível.',
            ]}
            nextSteps={[
              'Analisar a evolução por clube para identificar centros de excelência.',
              'Comparar tempos brasileiros com médias sul-americanas e mundiais.',
              'Aumentar cobertura de dados para categorias com séries incompletas.',
            ]}
          />
        )}

        {/* Methodology */}
        <StudyMethodologyBox
          methodology={
            meta.methodology ||
            'Este estudo analisa a evolução dos tempos médios em competições oficiais de natação no Brasil. Os dados são extraídos de PDFs oficiais processados pela ArenaStats e agregados pela view vw_swim_avg_by_category, que calcula média, melhor tempo, desvio padrão e contagem por combinação de evento, gênero, categoria etária e ano.'
          }
          dataSource={meta.data_source || 'ArenaStats — Competições oficiais de natação (PDFs)'}
          dataPoints={meta.total_data_points}
          period={
            summaryStats && summaryStats.yearRange.min !== Infinity
              ? `${summaryStats.yearRange.min}–${summaryStats.yearRange.max}`
              : undefined
          }
          limitations={[
            'Nem todas as competições brasileiras estão no banco de dados — a cobertura depende da disponibilidade de PDFs oficiais.',
            'Sub-categorias (1/2) dependem de data de nascimento — ~45% dos nadadores de base não possuem, ficando na categoria genérica (ex: "Infantil" sem número).',
            'Tempos de revezamento (relay) são excluídos desta análise pois usam partida lançada (flying start).',
            'Categorias de base (Petiz, Mirim) podem ter menos registros, impactando a representatividade das médias.',
          ]}
        />

        {/* Footer */}
        <StudyFooter />
      </div>
    </div>
  );
};

export default StudyAvgTimesEvolution;
